import React from "react";
import paper from "paper";
import { fixedState } from "./DecksState";

export const CANVAS_SIZE = 550;

export function getRect2Orientation(rotation, horizontal = false) {
    const orientations = { 0: "right", 90: "bottom", 180: "left", 270: "top" };
    if (horizontal) rotation += 90;
    rotation = rotation % 360; // Wrap around if rotation is 360 or more
    return orientations[rotation] || "right";
}

export function DrawDeck({
    canvasRef,
    textFields,
    deckShape,
    deckRotation,
    deckState,
    updateDeckState,
    hideText,
    hideGrid,
    showDeckLay,
    clickAttachments,
    textEditable,
}) {
    // TODO: move rect1, rect2, lShapeGroup to state handler
    let rect1, rect2, lShapeGroup;

    let rect1Length = deckState.deckStructure.lengthA;
    let rect1Height = deckState.deckStructure.widthA;
    let rect2Length = deckState.deckStructure.lengthB;
    let rect2Height = deckState.deckStructure.widthB;

    const shapeProperties = {
        "Rectangle / Square": { minWidth: 100, minHeight: 100 },
        "L Shape": { minWidth: 200, minHeight: 100 },
    };

    // Gets the deck colour from state and returns the HEX code
    const getDeckColour = () => {
        switch (deckState.deckMaterial) {
            case fixedState.deckMaterial[0]:
                return "#F2E3D1";
            case fixedState.deckMaterial[1]:
                return "#A1662F";
            case fixedState.deckMaterial[2]:
                return "#808076";
            case fixedState.deckMaterial[3]:
                return "#c0d0a3";
            default:
                return "orange";
        }
    };

    // Draws a 2D Grid onto the canvas
    const drawGrid = () => {
        const gridStep = 10; // Adjust this value to control grid density
        const gridLineColor = "lightgrey";
        const gridSize = CANVAS_SIZE;

        for (let x = 0; x <= gridSize; x += gridStep) {
            const from = new paper.Point(x, 0);
            const to = new paper.Point(x, gridSize);
            const line = new paper.Path.Line(from, to);
            line.strokeColor = gridLineColor;

            if (x % 50 === 0) {
                // Label every 50mm
                //   const text = new paper.PointText({
                //     point: from.add([0, 15]),
                //     content: (x / scaleFactor).toFixed(1) + " mm",
                //     fillColor: "black",
                //     fontSize: 14,
                //     fontFamily: "Arial",
                //     fontWeight: "bold",
                //   });
                //   text.position.x = x;
            }
        }

        for (let y = 0; y <= gridSize; y += gridStep) {
            const from = new paper.Point(0, y);
            const to = new paper.Point(gridSize, y);
            const line = new paper.Path.Line(from, to);
            line.strokeColor = gridLineColor;

            if (y % 50 === 0) {
                // Label every 50mm
                //   const text = new paper.PointText({
                //     point: from.add([-30, 0]),
                //     content: (y / scaleFactor).toFixed(1) + " mm",
                //     fillColor: "black",
                //     fontSize: 14,
                //     fontFamily: "Arial",
                //     fontWeight: "bold",
                //   });
                //   text.position.y = y;
            }
        }
    };

    // Adds the attachmentLine from deckState based on the rectangle and orientation
    function addAttachmentLines(name, rectangle, attachments) {
        const sides = ["top", "bottom", "left", "right"];

        sides.forEach((side) => {
            if (attachments[side] === true) {
                addLineToSide(name, rectangle, side);
            }
        });
    }

    // Define a function to create a new black dashed line
    function createDashedLine() {
        const line = new paper.Path();
        line.strokeColor = "black";
        line.dashArray = [15, 5];
        return line;
    }
    // Define a function to calculate the line endpoints for the "top" side
    function calculateTopSideLineEndpoints({ name, rectangle, orientation }) {
        const line = createDashedLine();

        if (rect1 && !rect2) {
            lineTop({ name, line });
        } else {
            if (orientation === "right") {
                lineTop({ name, line, caseBoth: true });
            } else if (orientation === "bottom") {
                if (name === "rect2") lineTop({ name, line, orientation, half: true });
                else lineTop({ name, line, orientation });
            } else if (orientation === "top") {
                if (name === "rect2") lineTop({ name, line });
            } else {
                lineTop({ name, line });
            }
        }

        return line;
    }
    // Define a function to calculate the line endpoints for the "bottom" side
    function calculateBottomSideLineEndpoints({ rectangle, name, orientation }) {
        const line = createDashedLine();

        if (rect1 && !rect2) {
            lineBottom({ name, line });
        } else {
            if (orientation === "bottom") {
                if (name === "rect2") lineBottom({ name, line });
            } else if (orientation === "left") {
                lineBottom({ name, line, caseBoth: true });
            } else if (orientation === "top") {
                if (name === "rect2") lineBottom({ name, line, half: true });
                else lineBottom({ name, line });
            } else {
                lineBottom({ name, line });
            }
        }

        return line;
    }
    // Define a function to calculate the line endpoints for the "left" side
    function calculateLeftSideLineEndpoints({ rectangle, name, orientation }) {
        const line = createDashedLine();
        const rectBounds = rectangle.bounds;

        if (rect1 && !rect2) {
            lineLeft({ line, name });
        } else {
            if (orientation === "top") {
                lineLeft({ rect1, rect2, caseBoth: true, line });
            } else if (orientation === "right") {
                if (name === "rect2") lineLeft({ name, line, half: true });
                else lineLeft({ name, line });
            } else if (orientation === "left") {
                if (name === "rect2") {
                    lineLeft({ line, name });
                }
            } else {
                lineLeft({ line, name });
            }
        }
        return line;
    }
    // Define a function to calculate the line endpoints for the "right" side
    function calculateRightSideLineEndpoints({ rectangle, name, orientation }) {
        const line = createDashedLine();

        if (rect1 && !rect2) {
            lineRight({ name, line });
        } else {
            if (orientation === "right" && name === "rect2") {
                lineRight({ name, line });
            } else if (orientation === "bottom") {
                lineRight({ name, line, caseBoth: true });
            } else if (orientation === "left") {
                if (name === "rect2") lineRight({ name, line, half: true });
                else lineRight({ name, line });
            } else if (orientation === "top") {
                lineRight({ name, line });
            }
        }

        return line;
    }

    function lineLeft({ name, caseBoth = false, line, half = false }) {
        // Create the line for both with handling
        if (
            caseBoth &&
            deckState.deckAttachments.rect2.left === true &&
            deckState.deckAttachments.rect1.left === true &&
            deckState.deckStructure.deckShape === fixedState.deckStructure.deckShape[1]
        ) {
            return null; // No line to add
        } else if (caseBoth) {
            line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, rect2.bounds.height / 2]));
            line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
        } else if (name === "rect1") {
            if (half) {
                line.add(
                    rect1.bounds.center.subtract([
                        rect1.bounds.width / 2,
                        rect1.bounds.height / 2 - rect2.bounds.height,
                    ])
                );
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
            } else {
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, rect1.bounds.height / 2]));
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
            }
        } else if (name === "rect2") {
            if (half) {
                line.add(
                    rect2.bounds.center.subtract([
                        rect2.bounds.width / 2,
                        rect2.bounds.height / 2 - rect1.bounds.height,
                    ])
                );
                line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            } else {
                line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, rect2.bounds.height / 2]));
                line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            }
        }
    }
    function lineRight({ name, caseBoth = false, line, half = false }) {
        // Create the line for both with handling
        if (
            caseBoth &&
            deckState.deckAttachments.rect2.right === true &&
            deckState.deckAttachments.rect1.right === true &&
            deckState.deckStructure.deckShape === fixedState.deckStructure.deckShape[1]
        ) {
            return null; // No line to add
        } else if (caseBoth) {
            line.add(rect2.bounds.center.add([rect2.bounds.width / 2, rect2.bounds.height / 2]));
            line.add(rect1.bounds.center.add([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
        } else if (name === "rect1") {
            if (half) {
                line.add(
                    rect1.bounds.center.add([rect1.bounds.width / 2, rect1.bounds.height / 2 - rect2.bounds.height])
                );
                line.add(rect1.bounds.center.add([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
            } else {
                line.add(rect1.bounds.center.add([rect1.bounds.width / 2, rect1.bounds.height / 2]));
                line.add(rect1.bounds.center.add([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
            }
        } else if (name === "rect2") {
            if (half) {
                line.add(
                    rect2.bounds.center.add([rect2.bounds.width / 2, rect2.bounds.height / 2 - rect1.bounds.height])
                );
                line.add(rect2.bounds.center.add([rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            } else {
                line.add(rect2.bounds.center.add([rect2.bounds.width / 2, rect2.bounds.height / 2]));
                line.add(rect2.bounds.center.add([rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            }
        }
    }
    function lineBottom({ name, caseBoth = false, line, half = false }) {
        // Create the line for both with handling
        if (
            caseBoth &&
            deckState.deckAttachments.rect2.bottom === true &&
            deckState.deckAttachments.rect1.bottom === true &&
            deckState.deckStructure.deckShape === fixedState.deckStructure.deckShape[1]
        ) {
            return null; // No line to add
        } else if (caseBoth) {
            line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            line.add(rect1.bounds.center.subtract([-rect1.bounds.width / 2, -rect1.bounds.height / 2]));
        } else if (name === "rect1") {
            if (half) {
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
                line.add(
                    rect1.bounds.center.subtract([
                        rect1.bounds.width / 2 - rect2.bounds.width,
                        -rect1.bounds.height / 2,
                    ])
                );
            } else {
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, -rect1.bounds.height / 2]));
                line.add(rect1.bounds.center.subtract([-rect1.bounds.width / 2, -rect1.bounds.height / 2]));
            }
        } else if (name === "rect2") {
            if (half) {
                line.add(
                    rect2.bounds.center.subtract([
                        rect2.bounds.width / 2 - rect1.bounds.width,
                        -rect2.bounds.height / 2,
                    ])
                );
                line.add(rect2.bounds.center.subtract([-rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            } else {
                line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, -rect2.bounds.height / 2]));
                line.add(rect2.bounds.center.subtract([-rect2.bounds.width / 2, -rect2.bounds.height / 2]));
            }
        }
    }
    function lineTop({ name, caseBoth = false, line, half = false }) {
        // Create the line for both with handling
        if (
            caseBoth &&
            deckState.deckAttachments.rect2.top === true &&
            deckState.deckAttachments.rect1.top === true &&
            deckState.deckStructure.deckShape === fixedState.deckStructure.deckShape[1]
        ) {
            return null; // No line to add
        } else if (caseBoth) {
            line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, rect1.bounds.height / 2]));
            line.add(rect2.bounds.center.subtract([-rect2.bounds.width / 2, rect2.bounds.height / 2]));
        } else if (name === "rect1") {
            if (half) {
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, rect1.bounds.height / 2]));
                line.add(rect1.bounds.center.subtract([-rect1.bounds.width / 2, rect1.bounds.height / 2]));
            } else {
                line.add(rect1.bounds.center.subtract([rect1.bounds.width / 2, rect1.bounds.height / 2]));
                line.add(rect1.bounds.center.subtract([-rect1.bounds.width / 2, rect1.bounds.height / 2]));
            }
        } else if (name === "rect2") {
            if (half) {
                line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, rect2.bounds.height / 2]));
                line.add(
                    rect2.bounds.center.subtract([
                        -rect2.bounds.width / 2 + rect1.bounds.width,
                        rect2.bounds.height / 2,
                    ])
                );
            } else {
                line.add(rect2.bounds.center.subtract([rect2.bounds.width / 2, rect2.bounds.height / 2]));
                line.add(rect2.bounds.center.subtract([-rect2.bounds.width / 2, rect2.bounds.height / 2]));
            }
        }
    }

    // Define the main function to add a line to a specified side of the rectangle
    function addLineToSide(name, rectangle, side) {
        const orientation = getRect2Orientation(deckState.deckStructure.deckRotation);

        const setLineProperties = (line) => {
            line.strokeWidth = 2;
            return line;
        };

        switch (side) {
            case "top":
                return setLineProperties(calculateTopSideLineEndpoints({ rectangle, name, orientation }));
            case "bottom":
                return setLineProperties(calculateBottomSideLineEndpoints({ rectangle, name, orientation }));
            case "left":
                return setLineProperties(calculateLeftSideLineEndpoints({ rectangle, name, orientation }));
            case "right":
                return setLineProperties(calculateRightSideLineEndpoints({ rectangle, name, orientation }));
            default:
                // Handle invalid side input
                console.error("Invalid side specified");
                return null;
        }
    }

    // Determines what side should be choosen based on rules -
    // i.e. if side clicked inbetween rectangle return either adjacent sides based on click position
    const determineSideClicked = ({ relativeMousePos, touchBoundX, touchBoundY, orientation, rectName }) => {
        let attachmentSide;
        const leftSide = relativeMousePos.x < touchBoundX;
        const rightSide = relativeMousePos.x > touchBoundX * 3;
        const topSide = relativeMousePos.y < touchBoundY;
        const bottomSide = relativeMousePos.y > touchBoundY * 3;

        if (leftSide) {
            attachmentSide = "left";
            if (orientation === "left" && rectName === "rect1") {
                if (relativeMousePos.y < rect1.bounds.height / 2) attachmentSide = "top";
                else attachmentSide = "bottom";
            }
            if (orientation === "right" && rectName === "rect2") {
                if (relativeMousePos.y < rect1.bounds.height / 2) attachmentSide = "top";
            }
        } else if (rightSide) {
            attachmentSide = "right";
            if (orientation === "right" && rectName === "rect1") {
                if (relativeMousePos.y < rect1.bounds.height / 2) attachmentSide = "top";
                else attachmentSide = "bottom";
            }
            if (orientation === "left" && rectName === "rect2") {
                // If in standard orientation then left clicks changed to top
                if (relativeMousePos.y < rect1.bounds.height / 2) attachmentSide = "bottom";
            }
        } else if (topSide) {
            attachmentSide = "top";
        } else if (bottomSide) {
            attachmentSide = "bottom";
        }

        return attachmentSide;
    };

    // Generates the attachment side and updates the state of which side was clicked based on position and rectangle.
    const attachSideClicked = (relativeMousePos, rect, rectName) => {
        const touchBoundX = rect.bounds.width / 4;
        const touchBoundY = rect.bounds.height / 4;

        let orientation;
        // L - Shape - Apply extra rules
        if (rect1 && rect2) {
            orientation = getRect2Orientation(deckRotation);
        }

        // Returns the side clicked on rect depending on orientation and rules
        return determineSideClicked({
            relativeMousePos,
            touchBoundX,
            touchBoundY,
            rectName,
            orientation,
        });
    };

    // Canvas Hover handler for attachments
    const handleCanvasMove = (event) => {
        if (clickAttachments) {
            const canvas = canvasRef.current;

            // Get the mouse position relative to the canvas
            const canvasRect = canvas.getBoundingClientRect();
            const mouseX = event.clientX - canvasRect.left;
            const mouseY = event.clientY - canvasRect.top;

            // Convert the mouse position to Paper.js coordinates
            const clickPoint = new paper.Point(mouseX, mouseY);

            // Check if the click occurred inside rect1
            if (rect1 && rect1.contains(clickPoint)) {
                // Calculate the attachment side based on the mouse position
                const relativeMousePos = clickPoint.subtract(rect1.bounds.topLeft);
                let attachmentSide = attachSideClicked(relativeMousePos, rect1, "rect1");

                let attachSide = true;
                if (deckState.deckAttachments.rect1[attachmentSide]) attachSide = false;

                updateDeckState({
                    ...deckState,
                    deckAttachments: {
                        ...deckState.deckAttachments,
                        rect1Hover: {
                            [attachmentSide]: attachSide, // Update the specific side
                        },
                    },
                });
            } else if (rect2 && rect2.contains(clickPoint)) {
                // Calculate the attachment side based on the mouse position
                const relativeMousePos = clickPoint.subtract(rect2.bounds.topLeft);
                let attachmentSide = attachSideClicked(relativeMousePos, rect2, "rect2");

                let attachSide = true;
                if (deckState.deckAttachments.rect2[attachmentSide]) attachSide = false;

                updateDeckState({
                    ...deckState,
                    deckAttachments: {
                        ...deckState.deckAttachments,
                        rect2Hover: {
                            [attachmentSide]: attachSide, // Update the specific side
                        },
                    },
                });
            }
        }
    };

    // Canvas click handler for attachments
    const handleCanvasClick = (event) => {
        if (clickAttachments) {
            const canvas = canvasRef.current;

            // Get the mouse click position relative to the canvas
            const canvasRect = canvas.getBoundingClientRect();
            const mouseX = event.clientX - canvasRect.left;
            const mouseY = event.clientY - canvasRect.top;

            // Convert the mouse position to Paper.js coordinates
            const clickPoint = new paper.Point(mouseX, mouseY);

            // Define the distance threshold for extending clicking bounds
            const clickThreshold = 30;

            // Check if the click occurred near rect1
            if (rect1 && isClickNearShape(clickPoint, rect1, clickThreshold)) {
                // Calculate the attachment side based on the mouse position
                const relativeMousePos = clickPoint.subtract(rect1.bounds.topLeft);
                let attachmentSide = attachSideClicked(relativeMousePos, rect1, "rect1");

                let attachSide = true;
                if (deckState.deckAttachments.rect1[attachmentSide]) attachSide = false;

                updateDeckState({
                    ...deckState,
                    deckAttachments: {
                        ...deckState.deckAttachments,
                        rect1: {
                            ...deckState.deckAttachments.rect1, // Keep the existing settings for rect1
                            [attachmentSide]: attachSide, // Update the specific side
                        },
                    },
                });
            } else if (rect2 && isClickNearShape(clickPoint, rect2, clickThreshold)) {
                // Calculate the attachment side based on the mouse position
                const relativeMousePos = clickPoint.subtract(rect2.bounds.topLeft);
                let attachmentSide = attachSideClicked(relativeMousePos, rect2, "rect2");

                let attachSide = true;
                if (deckState.deckAttachments.rect2[attachmentSide]) attachSide = false;

                updateDeckState({
                    ...deckState,
                    deckAttachments: {
                        ...deckState.deckAttachments,
                        rect2: {
                            ...deckState.deckAttachments.rect2, // Keep the existing settings for rect1
                            [attachmentSide]: attachSide, // Update the specific side
                        },
                    },
                });
            }
        }
    };

    // Function to check if a click is near a shape
    const isClickNearShape = (clickPoint, shape, threshold) => {
        const bounds = shape.bounds;
        const left = bounds.left - threshold;
        const right = bounds.right + threshold;
        const top = bounds.top - threshold;
        const bottom = bounds.bottom + threshold;

        return clickPoint.x >= left && clickPoint.x <= right && clickPoint.y >= top && clickPoint.y <= bottom;
    };

    const posMap = ["leftCenter", "topCenter", "rightCenter", "bottomCenter"];

    // Calculate the relative text position based on the deck rotation
    const getRelativeTextPos = (rectBounds, startPos, rotation) => {
        const startPosIndex = posMap.indexOf(startPos);

        if (startPosIndex !== -1) {
            // Calculate the adjusted rotation angle (take the absolute value)
            const absRotation = Math.abs(rotation % 360);

            if (absRotation === 90) {
                const nextPosIndex = (startPosIndex + 1) % posMap.length;
                return rectBounds[posMap[nextPosIndex]];
            } else if (absRotation === 180) {
                // Handle 180-degree rotation (opposite side)
                const oppositePosIndex = (startPosIndex + 2) % posMap.length;
                return rectBounds[posMap[oppositePosIndex]];
            } else if (absRotation === 270) {
                // Handle 270-degree rotation
                const previousPosIndex = (startPosIndex + 3) % posMap.length;
                return rectBounds[posMap[previousPosIndex]];
            } else {
                // Handle no rotation (0-degree rotation)
                return rectBounds[startPos];
            }
        } else {
            // Handle an invalid startPos
            return null; // or any appropriate default value
        }
    };

    // Main useEffect, updates and renderes the deck for canvas
    React.useEffect(() => {
        paper.setup(canvasRef.current);
        // Inside the drawDeck function

        const drawDeck = () => {
            const properties = shapeProperties[deckShape];
            let scaleFactor;
            if (deckShape === "Rectangle / Square") {
                // Draw Rectangle or Square logic (similar to your previous code)
                // Draw the first rectangle for the L Shape
                const rect1Width = rect1Length;
                const rect1HeightVal = rect1Height;

                const maxRectWidth = Math.max(rect1Width);
                const maxRectHeight = Math.max(rect1HeightVal);

                scaleFactor = Math.min(1, CANVAS_SIZE / 1.2 / (maxRectWidth + maxRectHeight));

                const scaledWidth1 = rect1Width * scaleFactor;
                const scaledHeight1 = rect1HeightVal * scaleFactor;

                const canvasCenterX = CANVAS_SIZE / 2;
                const canvasCenterY = CANVAS_SIZE / 2;

                rect1 = new paper.Path.Rectangle({
                    point: [canvasCenterX - scaledWidth1 / 2, canvasCenterY - scaledHeight1 / 2],
                    size: [scaledWidth1, scaledHeight1],
                    fillColor: getDeckColour(),
                    rotation: deckRotation,
                    draggable: true,
                });

                if (deckState.deckAttachments.rect1) {
                    addAttachmentLines("rect1", rect1, deckState.deckAttachments.rect1);
                }

                lShapeGroup = new paper.Group([rect1]);

                if (showDeckLay) {
                    // Create a group to hold the directional lines
                    const directionalLinesGroup = new paper.Group();

                    // Calculate the bounds of the entire lShapeGroup
                    const lShapeBounds = lShapeGroup.bounds;

                    // Function to get the left and right positions at a specific y-coordinate
                    function getXPositions(y) {
                        let left = null;
                        let right = null;

                        lShapeGroup.children.forEach((path) => {
                            const bounds = path.bounds;

                            if (y >= bounds.top && y <= bounds.bottom) {
                                if (left === null || bounds.left < left) {
                                    left = bounds.left;
                                }
                                if (right === null || bounds.right > right) {
                                    right = bounds.right;
                                }
                            }
                        });

                        return { left, right };
                    }

                    // Function to get the top and bottom positions at a specific x-coordinate
                    function getYPositions(x) {
                        let top = null;
                        let bottom = null;

                        lShapeGroup.children.forEach((path) => {
                            const bounds = path.bounds;

                            if (x >= bounds.left && x <= bounds.right) {
                                if (top === null || bounds.top < top) {
                                    top = bounds.top;
                                }
                                if (bottom === null || bounds.bottom > bottom) {
                                    bottom = bounds.bottom;
                                }
                            }
                        });

                        return { top, bottom };
                    }

                    // Loop to draw unconnected directional lines across the entire lShapeGroup
                    for (let position = lShapeBounds.left; position <= lShapeBounds.right; position += 40) {
                        let startPoint, endPoint;

                        const { left, right } = getXPositions(position);
                        if (left !== null && right !== null) {
                            startPoint = new paper.Point(left, position);
                            endPoint = new paper.Point(right, position);
                        }

                        if (startPoint && endPoint) {
                            // Create the directional line path
                            const directionalLine = new paper.Path.Line(startPoint, endPoint);

                            directionalLine.strokeColor = "orange"; // Set the stroke color
                            directionalLine.strokeWidth = 0.5; // Set the stroke width
                            directionalLine.dashArray = [4, 4]; // Set the dash array to create dotted lines

                            // Add the directional line to the directionalLinesGroup
                            directionalLinesGroup.addChild(directionalLine);
                        }
                    }

                    // Loop to draw unconnected directional lines across the entire lShapeGroup
                    for (let position = lShapeBounds.left; position <= lShapeBounds.right; position += 20) {
                        let startPoint, endPoint;

                        const { top, bottom } = getYPositions(position);
                        if (top !== null && bottom !== null) {
                            startPoint = new paper.Point(position, top);
                            endPoint = new paper.Point(position, bottom);
                        }

                        if (startPoint && endPoint) {
                            // Create the directional line path
                            const directionalLine = new paper.Path.Line(startPoint, endPoint);

                            directionalLine.strokeColor = "blue"; // Set the stroke color
                            directionalLine.strokeWidth = 1; // Set the stroke width

                            // Add the directional line to the directionalLinesGroup
                            directionalLinesGroup.addChild(directionalLine);
                        }
                    }

                    // Add the directional lines group to the lShapeGroup
                    lShapeGroup.addChild(directionalLinesGroup);
                }

                if (!hideText) {
                    createEditableText(
                        getRelativeTextPos(rect1.bounds, "leftCenter", deckRotation),
                        "rect1Height",
                        rect1HeightVal,
                        (newHeight) =>
                            updateDeckState({
                                ...deckState,
                                deckStructure: {
                                    ...deckState.deckStructure,
                                    widthA: Math.min(parseInt(newHeight), 30000),
                                },
                            })
                    );

                    createEditableText(
                        getRelativeTextPos(rect1.bounds, "bottomCenter", deckRotation),
                        "rect1Length",
                        rect1Width,
                        (newHeight) =>
                            updateDeckState({
                                ...deckState,
                                deckStructure: {
                                    ...deckState.deckStructure,
                                    lengthA: Math.min(parseInt(newHeight), 30000),
                                },
                            })
                    );
                }
            } else if (deckShape === "L Shape") {
                // Draw the first rectangle for the L Shape
                const rect1Width = rect1Length;
                const rect1HeightVal = rect1Height;

                const maxRectWidth = Math.max(rect1Width, rect2Length);
                const maxRectHeight = Math.max(rect1HeightVal, rect2Height);

                const maxCanvasSize = CANVAS_SIZE - 100; // Adjust somePadding as needed

                const scaleFactorWidth = maxCanvasSize / (rect1Width + rect2Length);
                const scaleFactorHeight = maxCanvasSize / (rect1HeightVal + rect2Height);

                // Choose the minimum scale factor between width and height to ensure both dimensions fit within the canvas
                const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);

                const scaledWidth1 = rect1Width * scaleFactor;
                const scaledHeight1 = rect1HeightVal * scaleFactor;

                const canvasCenterX = CANVAS_SIZE / 2;
                const canvasCenterY = CANVAS_SIZE / 2;

                // Calculate rect2Width based on the aspect ratio of rect1
                const rect2HeightVal = rect2Height;
                const rect2Width = rect2Length;

                const scaledWidth2 = rect2Width * scaleFactor;
                const scaledHeight2 = rect2HeightVal * scaleFactor;

                const xOffset = (rect1Width * scaleFactor - scaledWidth1 + scaledWidth2) / 2;

                const yOffset = (maxRectHeight * scaleFactor - scaledHeight1) / 2;

                rect1 = new paper.Path.Rectangle({
                    point: [canvasCenterX - scaledWidth1 / 2 - xOffset, canvasCenterY - scaledHeight1 / 2 - yOffset],
                    size: [scaledWidth1, scaledHeight1],
                    fillColor: getDeckColour(),
                    draggable: true,
                });

                // Calculate the position and dimensions for the second rectangle
                const rect2X = rect1.bounds.topRight.x - 1;
                const rect2Y = rect1.bounds.topRight.y;

                rect2 = new paper.Path.Rectangle({
                    point: [rect2X, rect2Y],
                    size: [scaledWidth2, scaledHeight2],
                    fillColor: getDeckColour(),
                    // draggable: true,
                });

                lShapeGroup = new paper.Group([rect1, rect2]);

                // Calculate the center of the "L Shape"
                const lShapeCenter = lShapeGroup.bounds.center;

                // Apply rotation to the entire "L Shape"
                lShapeGroup.rotate(deckRotation, lShapeCenter);

                if (showDeckLay) {
                    // Create a group to hold the directional lines
                    const directionalLinesGroup = new paper.Group();

                    // Calculate the bounds of the entire lShapeGroup
                    const lShapeBounds = lShapeGroup.bounds;

                    // Function to get the left and right positions at a specific y-coordinate
                    function getXPositions(y) {
                        let left = null;
                        let right = null;

                        lShapeGroup.children.forEach((path) => {
                            const bounds = path.bounds;

                            if (y >= bounds.top && y <= bounds.bottom) {
                                if (left === null || bounds.left < left) {
                                    left = bounds.left;
                                }
                                if (right === null || bounds.right > right) {
                                    right = bounds.right;
                                }
                            }
                        });

                        return { left, right };
                    }

                    // Function to get the top and bottom positions at a specific x-coordinate
                    function getYPositions(x) {
                        let top = null;
                        let bottom = null;

                        lShapeGroup.children.forEach((path) => {
                            const bounds = path.bounds;

                            if (x >= bounds.left && x <= bounds.right) {
                                if (top === null || bounds.top < top) {
                                    top = bounds.top;
                                }
                                if (bottom === null || bounds.bottom > bottom) {
                                    bottom = bounds.bottom;
                                }
                            }
                        });

                        return { top, bottom };
                    }

                    // Loop to draw unconnected directional lines across the entire lShapeGroup
                    for (let position = lShapeBounds.left; position <= lShapeBounds.right; position += 40) {
                        let startPoint, endPoint;

                        const { left, right } = getXPositions(position);
                        if (left !== null && right !== null) {
                            startPoint = new paper.Point(left, position);
                            endPoint = new paper.Point(right, position);
                        }

                        if (startPoint && endPoint) {
                            // Create the directional line path
                            const directionalLine = new paper.Path.Line(startPoint, endPoint);

                            directionalLine.strokeColor = "orange"; // Set the stroke color
                            directionalLine.strokeWidth = 0.5; // Set the stroke width
                            directionalLine.dashArray = [4, 4]; // Set the dash array to create dotted lines

                            // Add the directional line to the directionalLinesGroup
                            directionalLinesGroup.addChild(directionalLine);
                        }
                    }

                    // Loop to draw unconnected directional lines across the entire lShapeGroup
                    for (let position = lShapeBounds.left; position <= lShapeBounds.right; position += 20) {
                        let startPoint, endPoint;

                        const { top, bottom } = getYPositions(position);
                        if (top !== null && bottom !== null) {
                            startPoint = new paper.Point(position, top);
                            endPoint = new paper.Point(position, bottom);
                        }

                        if (startPoint && endPoint) {
                            // Create the directional line path
                            const directionalLine = new paper.Path.Line(startPoint, endPoint);

                            directionalLine.strokeColor = "blue"; // Set the stroke color
                            directionalLine.strokeWidth = 1; // Set the stroke width

                            // Add the directional line to the directionalLinesGroup
                            directionalLinesGroup.addChild(directionalLine);
                        }
                    }

                    // Add the directional lines group to the lShapeGroup
                    lShapeGroup.addChild(directionalLinesGroup);
                }

                // Add the attachments
                if (deckState.deckAttachments.rect1 && rect1 && rect2) {
                    addAttachmentLines("rect1", rect1, deckState.deckAttachments.rect1);
                }

                if (deckState.deckAttachments.rect2 && rect1 && rect2) {
                    addAttachmentLines("rect2", rect2, deckState.deckAttachments.rect2);
                }

                if (!hideText) {
                    createEditableText(
                        getRelativeTextPos(rect1.bounds, "leftCenter", deckRotation),
                        "rect1Height",
                        rect1HeightVal,
                        (newHeight) =>
                            updateDeckState({
                                ...deckState,
                                deckStructure: {
                                    ...deckState.deckStructure,
                                    widthA: Math.min(parseInt(newHeight), 30000),
                                },
                            })
                    );

                    createEditableText(
                        getRelativeTextPos(rect1.bounds, "bottomCenter", deckRotation),
                        "rect1Length",
                        rect1Width,
                        (newHeight) =>
                            updateDeckState({
                                ...deckState,
                                deckStructure: {
                                    ...deckState.deckStructure,
                                    lengthA: Math.min(parseInt(newHeight), 30000),
                                },
                            })
                    );
                }

                if (!hideText) {
                    createEditableText(
                        getRelativeTextPos(rect2.bounds, "rightCenter", deckRotation),
                        "rect2Height",
                        rect2HeightVal,
                        (newHeight) =>
                            updateDeckState({
                                ...deckState,
                                deckStructure: {
                                    ...deckState.deckStructure,
                                    widthB: Math.min(parseInt(newHeight), 30000),
                                },
                            })
                    );

                    createEditableText(
                        getRelativeTextPos(rect2.bounds, "bottomCenter", deckRotation),
                        "rect2Length",
                        rect2Width,
                        (newHeight) =>
                            updateDeckState({
                                ...deckState,
                                deckStructure: {
                                    ...deckState.deckStructure,
                                    lengthB: Math.min(parseInt(newHeight), 30000),
                                },
                            })
                    );
                }
            }
        };

        const createEditableText = (position, id, content, updateValue) => {
            // Remove the old input field if it exists
            const modalContainer = document.getElementById("deckbuilder");

            const oldInputField = textFields.current[id];
            if (oldInputField) {
                if (modalContainer) {
                    modalContainer.removeChild(oldInputField);
                } else {
                    console.warn("Could not find the modal container");
                }
            }

            content = Math.min(content, 30000);

            const textItem = new paper.PointText({
                point: position,
                content: content, // + "mm",
                justification: "center",
                fillColor: "black",
                fontSize: 16,
                fontFamily: "Arial",
                fontWeight: "bold",
            });

            const inputField = document.createElement("input");
            inputField.type = "text";
            //inputField.value = content; //+ "mm";
            inputField.id = id;
            inputField.style.position = "absolute";
            inputField.style.zIndex = "9999";
            inputField.style.display = "none";

            const applyValue = () => {
                let parsedValue = parseFloat(inputField.value);
                if (parsedValue > 30000) {
                    parsedValue = 30000;
                    inputField.value = "30000";
                }

                // Handle empty input
                if (isNaN(parsedValue) || inputField.value.trim() === "") {
                    inputField.style.display = "none";
                } else {
                    if (parsedValue < 100) {
                        parsedValue = 100;
                        inputField.value = "100";
                    }
                    textItem.content = parsedValue; //+ "mm";
                    inputField.style.display = "none";
                    updateValue(parsedValue);
                }
            };

            inputField.onblur = applyValue;

            inputField.addEventListener("keydown", (event) => {
                if (event.key === "Enter") {
                    applyValue();
                }
            });

            textItem.onMouseDown = function (event) {
                if (textEditable) {
                    event.preventDefault();

                    const inputField = textFields.current[id];

                    // Use the position property of the textItem
                    const position = textItem.position;

                    // Use absolute positioning for the input field
                    inputField.style.position = "absolute";
                    inputField.style.left = position.x + "px";
                    inputField.style.top = position.y + 50 + "px";
                    inputField.style.display = "block";
                    inputField.focus();
                }
            };

            textFields.current[id] = inputField;

            if (modalContainer) {
                modalContainer.appendChild(inputField);
            } else {
                console.warn("Could not find the modal container");
            }
        };

        paper.project.activeLayer.removeChildren();
        if (!hideGrid) drawGrid();
        drawDeck();
    }, [
        deckShape,
        rect1Length,
        rect1Height,
        rect2Length,
        rect2Height,
        deckRotation,
        deckState.deckMaterial,
        deckState.deckAttachments,
        deckState.deckStructure.deckDirection,
        deckState.deckStructure.deckAttachment,
    ]);

    // Returns the deck canvas, can be used directly or ref the canvas to get deck (not recommended).
    return (
        <canvas
            onClick={handleCanvasClick}
            // onMouseMove={handleCanvasMove}
            // onMouseLeave={() => {
            //   updateDeckState({
            //     ...deckState,
            //     deckAttachments: {
            //       ...deckState.deckAttachments,
            //       rect1Hover: {},
            //     },
            //   });
            //   updateDeckState({
            //     ...deckState,
            //     deckAttachments: {
            //       ...deckState.deckAttachments,
            //       rect2Hover: {},
            //     },
            //   });
            // }}
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
        />
    );
}

export default DrawDeck;
